// Civil Public Library — anonymised cross-org rate benchmark browser.
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Globe2, Search, RefreshCw, Copy, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const nzd = (n) => `$${Number(n).toLocaleString("en-NZ", { minimumFractionDigits: 2 })}`;

export default function CivilPublicLibrary() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/civil-library");
      setData(d);
    } catch { setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rebuild = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/civil-library/rebuild");
      toast.success(`Library rebuilt — ${r.items_count} benchmark items`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Rebuild failed"); }
    finally { setBusy(false); }
  };

  const setLaunch = async (v) => {
    try {
      await api.post("/civil-library/launch", { launched: v });
      setData((p) => ({ ...p, launched: v }));
      toast.success(v ? "Civil Public Library is now LIVE for all companies" : "Library hidden — super admin only");
    } catch { toast.error("Couldn't update launch state"); }
  };

  const copy = async (item) => {
    try {
      await api.post("/civil-library/copy", { item_id: item.id });
      toast.success(`"${item.description}" copied to your rate library at ${nzd(item.median)}/${item.unit}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Copy failed"); }
  };

  const items = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (data?.items || []).filter((i) =>
      (cat === "all" || i.category === cat) &&
      (!ql || i.description.toLowerCase().includes(ql)));
  }, [data, q, cat]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#FF5F15]" /></div>;
  if (!data) return <p className="text-sm text-zinc-500 py-10 text-center">The Civil Public Library isn't available for your account yet.</p>;

  return (
    <div data-testid="civil-public-library">
      <div className="flex items-start gap-3 mb-4 px-3 py-2.5 border border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-white/[0.03]">
        <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
        <p className="text-[11px] text-zinc-500">
          Anonymised market pricing built from quotes and rate libraries across all Fieldlink companies.
          Every item was priced by at least <b>2 different companies</b> — no company, client or job is ever identified.
        </p>
      </div>

      {isSuper && (
        <div className="flex flex-wrap items-center gap-3 mb-4 px-3 py-2.5 border border-[#FF5F15]/40 bg-[#FF5F15]/5" data-testid="civil-lib-admin-bar">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#FF5F15]">Platform controls</span>
          <Button size="sm" variant="outline" onClick={rebuild} disabled={busy}
            className="h-7 rounded-sm text-[10px] font-bold uppercase tracking-wider" data-testid="civil-lib-rebuild">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Rebuild now
          </Button>
          <span className="text-[10px] text-zinc-500 font-mono">
            {data.items_count} items{data.last_rebuilt ? ` · rebuilt ${new Date(data.last_rebuilt).toLocaleString("en-NZ")}` : ""} · auto-rebuilds daily
          </span>
          <div className="flex-1" />
          <label className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
            <Switch checked={!!data.launched} onCheckedChange={setLaunch} data-testid="civil-lib-launch-toggle" />
            {data.launched ? <span className="text-emerald-500">Live for all companies</span> : <span className="text-zinc-500">Super admin only (not launched)</span>}
          </label>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search market rates…"
            className="h-9 rounded-sm text-xs pl-8" data-testid="civil-lib-search" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)}
          className="h-9 px-2 rounded-sm text-xs border border-zinc-300 dark:border-white/15 bg-transparent dark:bg-[#0F141C]"
          data-testid="civil-lib-category">
          <option value="all">All categories</option>
          {(data.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {!items.length ? (
        <p className="text-sm text-zinc-500 py-10 text-center" data-testid="civil-lib-empty">
          No benchmark items yet — items appear once at least 2 companies have priced the same thing.
        </p>
      ) : (
        <div className="border border-zinc-200 dark:border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 border-b border-zinc-200 dark:border-white/10">
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-left px-2 py-2">Unit</th>
                <th className="text-right px-2 py-2">Low</th>
                <th className="text-right px-2 py-2">Median</th>
                <th className="text-right px-2 py-2">High</th>
                <th className="text-right px-2 py-2">Companies</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-white/5">
              {items.map((i) => (
                <tr key={i.id} className="hover:bg-zinc-50 dark:hover:bg-white/[0.03]" data-testid={`civil-lib-row-${i.key.replace(/\s+/g, "-").slice(0, 40)}`}>
                  <td className="px-3 py-2">
                    <p className="text-xs font-semibold">{i.description}</p>
                    <p className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">{i.category}</p>
                  </td>
                  <td className="px-2 py-2 text-xs font-mono text-zinc-500">{i.unit}</td>
                  <td className="px-2 py-2 text-xs font-mono text-right text-zinc-500">{nzd(i.low)}</td>
                  <td className="px-2 py-2 text-xs font-mono font-bold text-right text-[#FF5F15]">{nzd(i.median)}</td>
                  <td className="px-2 py-2 text-xs font-mono text-right text-zinc-500">{nzd(i.high)}</td>
                  <td className="px-2 py-2 text-xs font-mono text-right">{i.orgs}</td>
                  <td className="px-2 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => copy(i)}
                      className="h-7 rounded-sm text-[10px] font-bold uppercase tracking-wider"
                      data-testid={`civil-lib-copy-${i.key.replace(/\s+/g, "-").slice(0, 40)}`}>
                      <Copy className="w-3 h-3 mr-1" /> Copy
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
